import React, { useState, useEffect, useMemo } from "react";
import { useWizardContext } from "../useWizardContext";
import { useToast } from "../../../../context/ToastContext";
import { getExpertises, getSubExpertises } from "../../../../api/taxonomy";
import {
  Home, Coffee, PartyPopper, Truck, Camera, Users as UsersIcon, Shield, Plus,
  Disc, Mic2, Music, Speaker, Smile, Keyboard, User,
  Zap, Flag, Video, Star, ListPlus, Users2,
  CalendarCheck, Handshake, PiggyBank, Clock, Building2, Clapperboard,
  MapPin, UserCheck, UtensilsCrossed, FileCheck2, BedDouble, Plane,
  ClipboardList, BadgeCheck, Rocket, Megaphone, Palette, Store,
  Package, UserPlus, CalendarDays, UserCog, ClipboardCheck, BarChart2,
  TrendingUp, MapPinned, LayoutGrid, Navigation, Crown, Boxes, Cpu,
  CalendarRange, Ticket, Lightbulb, Volume2, Mail, GraduationCap,
  BookOpen, Mic, Trophy, ShieldCheck, PersonStanding, Piano, Search, Sparkles
} from "lucide-react";

const ICON_MAP = {
  // ── General ──────────────────────────────────────────────────────────────
  "CalendarCheck": CalendarCheck, "Handshake": Handshake, "PiggyBank": PiggyBank,
  "Clock": Clock, "Building2": Building2, "Truck": Truck, "Clapperboard": Clapperboard,
  "MapPin": MapPin, "Users2": Users2, "UserCheck": UserCheck, "Star": Star,
  "Mic2": Mic2, "PartyPopper": PartyPopper, "UtensilsCrossed": UtensilsCrossed,
  "Shield": Shield, "FileCheck2": FileCheck2,
  // ── Planner ───────────────────────────────────────────────────────────────
  "BedDouble": BedDouble, "Plane": Plane, "ClipboardList": ClipboardList,
  "Speaker": Speaker, "BadgeCheck": BadgeCheck, "Flag": Flag, "Rocket": Rocket,
  "Megaphone": Megaphone, "Users": UsersIcon, "Palette": Palette, "Store": Store,
  "Package": Package, "UserPlus": UserPlus, "CalendarDays": CalendarDays,
  "UserCog": UserCog, "ClipboardCheck": ClipboardCheck, "BarChart2": BarChart2,
  "TrendingUp": TrendingUp, "MapPinned": MapPinned, "LayoutGrid": LayoutGrid,
  "Navigation": Navigation, "Crown": Crown, "Boxes": Boxes, "Cpu": Cpu,
  "Music": Music, "CalendarRange": CalendarRange, "Ticket": Ticket,
  "Lightbulb": Lightbulb, "Volume2": Volume2, "Mail": Mail, "Camera": Camera,
  "GraduationCap": GraduationCap, "BookOpen": BookOpen, "Mic": Mic,
  "Trophy": Trophy, "ShieldCheck": ShieldCheck,
  // ── Performer ─────────────────────────────────────────────────────────────
  "Disc": Disc, "Smile": Smile, "Piano": Piano, "PersonStanding": PersonStanding,
  // ── Crew ──────────────────────────────────────────────────────────────────
  "Zap": Zap, "ListPlus": ListPlus, "Video": Video,

  // ── Taxonomy & Sub-Expertises ─────────────────────────────────────────────
  "End-to-End Event Planning": ClipboardList, "Vendor Coordination": UsersIcon,
  "Budget Planning": PiggyBank, "Timeline Planning": Clock, "Venue Management": Home,
  "Logistics Management": Truck, "Production Management": Zap, "On-ground Operations": MapPin,
  "Volunteer Coordination": Users2, "Guest Management": UserCheck, "Hospitality Management": Star,
  "Stage & AV Coordination": Mic2, "Décor Coordination": PartyPopper, "Catering Coordination": UtensilsCrossed,
  "Security Coordination": Shield, "Permissions & Compliance": FileCheck2, "Accommodation Management": BedDouble,
  "Travel Coordination": Plane, "Registration Management": ClipboardCheck, "VIP Protocol Coordination": Crown,
  "Branding & Signage": Flag,
  "3 Piece Band": Music, "4 Piece Band": Music, "5+ Piece Band": Music, "Wedding Band": Music, "Corporate Band": Music, "Party Band": Music,
  "Bollywood": Mic2, "Retro": Disc, "Punjabi": Music, "Sufi": Sparkles, "Ghazal": Mic, "Classical": Piano, "Semi - Classical": Piano, "Folk": Music, "Indie": Music, "Pop": Music, "Rock": Music, "Jazz": Music, "Acoustic": Volume2, "EDM": Disc, "Rap": Mic2, "Devotional": Sparkles, "Qawwali": Mic,
  "Commercial": Disc, "House": Disc, "Techno": Disc, "Trance": Disc, "Hip-Hop": Disc, "Wedding DJ": Disc, "Club DJ": Disc, "Corporate DJ": Disc,
  "Solo": PersonStanding, "Duo": UsersIcon, "Trio": Users2, "Dance Group": Users2, "Vocalist": Mic2, "Bhangra": UserCheck, "Belly Dance": Sparkles, "Salsa": Sparkles, "Kathak": PersonStanding, "Bharatnatyam": PersonStanding, "Freestyle": Sparkles, "LED Dance": Sparkles,
  "Wedding Anchor": Speaker, "Corporate Emcee": Speaker, "Conference Host": Speaker, "Award Show Host": Trophy, "Product Launch Host": Rocket, "College Fest Host": PartyPopper, "Concert Host": Mic2, "Sports Host": Trophy, "Government Event Anchor": Shield, "TV Host": Video, "Emcee / Anchor": Speaker,
  "Illusionist": Sparkles, "Mind Reader": Lightbulb, "Street Magic": Sparkles, "Close-up Magic": Sparkles, "Stage Magic": Sparkles, "Kids Magic": Smile, "Corporate Magician": Sparkles, "Trade Show Magician": Sparkles, "Fire Magic": Zap, "Digital / iPad Magic": Cpu, "Escape Artist": Sparkles, "Hypnotist": Sparkles, "Magician": Sparkles, "Mentalist": Lightbulb,
  "Voice Over Artist": Mic, "Celebrity Impressionist": Smile, "Mimicry Artist": Smile,
  "Kavi": BookOpen, "Shayari": BookOpen, "Standup Poet": BookOpen, "Mushaira": BookOpen, "Poet / Shayari Artist": BookOpen,
  "Ventriloquist": Smile, "Puppeteer": Smile, "Speed Painter": Palette, "Sand Artist": Palette, "Live Painter": Palette, "Caricature Artist": Palette,
  "Balloon Artist": PartyPopper, "Tattoo Artist": Palette, "Face Painter": Palette, "Mascot": Smile, "Kids Entertainer": Smile,
  "Stilt Walker": PersonStanding, "Fire Performer": Zap, "Aerialist": Sparkles, "Juggler": Sparkles, "Unicyclist": Sparkles, "Hula Hoop": Sparkles, "Contortionist": PersonStanding, "Living Statue": PersonStanding, "Bubble Artist": Sparkles, "Shadow Act": Sparkles, "Circus & Specialty Acts": Sparkles,
  "Celebrity Lookalike": Crown, "Event Model": Crown, "Clean Comedy": Smile, "Stand-up Comedy": Smile, "Improv": Smile,

  // ── Legacy ────────────────────────────────────────────────────────────────
  "Venue": Home, "Catering": Coffee, "Decor": PartyPopper, "Logistics": Truck,
  "Photography": Camera, "Coordination": UsersIcon, "Security": Shield,
  "DJ": Disc, "Singer": Mic2, "Live Band": Music, "Host/MC": Speaker,
  "Comedian": Smile, "Instrumentalist": Keyboard, "Dancer": User,
  "Runners": Truck, "Shadow": Zap, "Usher": Flag,
};

function checkIsSelected(srv, selectedList) {
  if (!selectedList || !Array.isArray(selectedList)) return false;
  const srvId = String(srv._id || srv.id || "");
  const srvName = String(srv.name || "").toLowerCase();

  return selectedList.some(s => {
    if (typeof s === 'object' && s !== null) {
      const sId = String(s._id || s.id || "");
      const sName = String(s.name || "").toLowerCase();
      return (srvId && sId && sId === srvId) || (srvName && sName && sName === srvName);
    }
    const strVal = String(s || "").toLowerCase();
    return (srvId && strVal === srvId.toLowerCase()) || (srvName && strVal === srvName);
  });
}

const getIconForService = (srv) => {
  if (!srv) return Sparkles;
  if (typeof srv === 'object' && (srv.isCustom || srv.isSuggestion)) return Plus;
  const name = typeof srv === 'string' ? srv : srv.name;
  const iconStr = typeof srv === 'object' && srv.icon ? srv.icon : null;

  if (name && ICON_MAP[name]) return ICON_MAP[name];
  if (iconStr && ICON_MAP[iconStr]) return ICON_MAP[iconStr];

  if (name) {
    const match = Object.keys(ICON_MAP).find(k => k.toLowerCase() === name.toLowerCase());
    if (match) return ICON_MAP[match];
  }
  if (iconStr) {
    const match = Object.keys(ICON_MAP).find(k => k.toLowerCase() === iconStr.toLowerCase());
    if (match) return ICON_MAP[match];
  }
  const isDbObject = typeof srv === 'object' && srv !== null && srv._id && /^[0-9a-fA-F]{24}$/.test(String(srv._id));
  if (name && typeof name === 'string' && !/^[0-9a-fA-F]{24}$/.test(name)) {
    if (!isDbObject) {
      return Plus;
    }
  }
  return Sparkles;
};

// ─── ServiceSelectionGrid is a TOP-LEVEL component with direct search and scrollable grid ───
function ServiceSelectionGrid({ title, options, loading, selectedServices, onToggle }) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredOptions = useMemo(() => {
    const query = (searchQuery || "").toLowerCase().trim();
    if (!query) return options || [];
    return (options || []).filter(opt =>
      (opt.name || "").toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  const hasExactMatch = useMemo(() => {
    if (!searchQuery.trim()) return true;
    return (options || []).some(opt => (opt.name || "").toLowerCase() === searchQuery.trim().toLowerCase());
  }, [options, searchQuery]);

  return (
    <div className="flex flex-col gap-2 sm:gap-2.5 w-full">
      <h3 className="text-base sm:text-lg font-bold text-gray-950">{title}</h3>

      {/* Direct Search Bar */}
      <div className="relative w-full">
        <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          placeholder="Search for a service, e.g. Photography, DJ, Catering"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 h-[36px] border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF4C18] text-xs sm:text-sm text-gray-900 bg-white placeholder:text-gray-400"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-6 border-2 border-dashed border-gray-200 rounded-xl">
          <p className="text-gray-400 text-xs sm:text-sm font-medium animate-pulse">Loading expertises...</p>
        </div>
      ) : (
        <div
          className="max-h-[190px] sm:max-h-[260px] overflow-y-auto pr-1 py-1"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#D1D5DB transparent' }}
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-2.5">
            {filteredOptions.map((srv) => {
              const isSelected = checkIsSelected(srv, selectedServices);
              const Icon = getIconForService(srv);
              return (
                <button
                  type="button"
                  key={srv._id || srv.id || srv.name}
                  onClick={() => onToggle(srv._id || srv.id || srv.name)}
                  className={`flex flex-col items-center justify-center p-2.5 rounded-xl border-[1.5px] text-center transition-all bg-white min-h-[85px] sm:min-h-[120px] cursor-pointer ${
                    isSelected
                      ? "border-[#FF4C18] text-[#FF4C18] shadow-[0_4px_16px_rgba(255,76,24,0.08)] font-bold"
                      : "border-transparent text-gray-900 shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] font-semibold"
                  }`}
                >
                  <Icon className={`w-5 h-5 mb-1.5 stroke-[1.8] ${isSelected ? "text-[#FF4C18]" : "text-gray-900"}`} />
                  <span className="text-[11px] sm:text-xs leading-tight break-words">{srv.name}</span>
                </button>
              );
            })}
            {!hasExactMatch && searchQuery.trim() && (
              <button
                type="button"
                onClick={() => {
                  onToggle(searchQuery.trim());
                  setSearchQuery("");
                }}
                className={`flex flex-col items-center justify-center p-2.5 rounded-xl border-[1.5px] text-center transition-all bg-white min-h-[85px] sm:min-h-[120px] cursor-pointer ${
                  checkIsSelected({ _id: searchQuery.trim(), name: searchQuery.trim() }, selectedServices)
                    ? "border-[#FF4C18] text-[#FF4C18] shadow-[0_4px_16px_rgba(255,76,24,0.08)] font-bold"
                    : "border-transparent text-gray-900 shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] font-semibold"
                }`}
              >
                <Plus className="w-5 h-5 mb-1.5 stroke-[1.8] text-[#FF4C18]" />
                <span className="text-[11px] sm:text-xs leading-tight break-words">Add "{searchQuery.trim()}"</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Step2_RoleSpecific() {
  const { formData, updateField, nextStep, prevStep } = useWizardContext();
  const { showError } = useToast();
  const role = formData.hiringRole;

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExpertises = async () => {
      try {
        setLoading(true);
        let targetCategoryIds = [];
        if (formData.eventTypeModel === 'SubCategory' && formData.eventCategoryData?.categories) {
          const cats = formData.eventCategoryData.categories;
          targetCategoryIds = (Array.isArray(cats) ? cats : [cats]).map(c => typeof c === 'object' && c !== null ? c._id || c.id : c).filter(Boolean);
        }
        if (targetCategoryIds.length === 0 && formData.eventType) {
          targetCategoryIds = [formData.eventType];
        }
        const res = await getExpertises(role, targetCategoryIds);
        const data = res?.data || [];
        setCategories(data);
      } catch (err) {
        console.error("Failed to fetch expertises:", err);
        showError("Failed to load expertises. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    if (role) {
      fetchExpertises();
    }
  }, [role, formData.eventType, formData.eventTypeModel, formData.eventCategoryData]);

  useEffect(() => {
    const fetchSubExp = async () => {
      const selectedServices = (formData.services || []).map(c => typeof c === 'object' && c !== null ? c._id || c.id || c.name : c).filter(Boolean);
      const currentSubServices = (formData.subServices || []).map(c => typeof c === 'object' && c !== null ? c._id || c.id || c.name : c).filter(Boolean);
      if (selectedServices.length === 0 && currentSubServices.length === 0) {
        setSubCategories([]);
        return;
      }
      try {
        const res = await getSubExpertises(selectedServices, currentSubServices);
        setSubCategories(res?.data || []);
      } catch (err) {
        console.error("Failed to fetch sub-expertises:", err);
      }
    };
    fetchSubExp();
  }, [formData.services, formData.subServices]);

  const toggleService = (serviceId) => {
    const currentServices = formData.services || [];
    const currentSubServices = formData.subServices || [];
    if (currentServices.includes(serviceId)) {
      updateField({ services: currentServices.filter(s => s !== serviceId) });
    } else {
      if (currentServices.length + currentSubServices.length >= 7) {
        showError("You can select a maximum of 7 expertises.");
        return;
      }
      updateField({ services: [...currentServices, serviceId] });
    }
  };

  const toggleSubService = (serviceId) => {
    const currentServices = formData.services || [];
    const currentSubServices = formData.subServices || [];
    if (currentSubServices.includes(serviceId)) {
      updateField({ subServices: currentSubServices.filter(s => s !== serviceId) });
    } else {
      if (currentServices.length + currentSubServices.length >= 7) {
        showError("You can select a maximum of 7 expertises.");
        return;
      }
      updateField({ subServices: [...currentSubServices, serviceId] });
    }
  };

  const handleNext = () => {
    if ((!formData.services || formData.services.length === 0) && (!formData.subServices || formData.subServices.length === 0)) {
      showError("Please select at least one expertise.");
      return;
    }
    if (role === 'planner' && (!formData.guests || formData.guests < 1)) {
      showError("Please specify the number of guests.");
      return;
    }
    if (role !== 'planner' && (!formData.durationHours || formData.durationHours < 1)) {
      showError("Please specify the duration in hours.");
      return;
    }
    if (role !== 'planner' && formData.durationHours > 24) {
      showError("Work duration cannot exceed 24 hours.");
      return;
    }
    nextStep();
  };

  const combinedOptions = [...categories, ...subCategories];
  const combinedSelected = [...(formData.services || []), ...(formData.subServices || [])];

  const handleCombinedToggle = (id) => {
    if ((formData.services || []).includes(id)) {
      toggleService(id);
      return;
    }
    if ((formData.subServices || []).includes(id)) {
      toggleSubService(id);
      return;
    }

    // Custom suggestion (not a DB item) — validate before adding
    const isKnownItem = [...categories, ...subCategories].some(
      (c) => c._id === id || c.id === id || c.name === id
    );
    if (!isKnownItem && typeof id === 'string') {
      const trimmed = id.trim();
      if (!trimmed) return;
      if (trimmed.replace(/\s+/g, ' ').split(' ').length > 3) {
        showError("Suggestion can be at most 3 words.");
        return;
      }
      if (trimmed.length > 35) {
        showError("Suggestion can be at most 35 characters.");
        return;
      }
    }

    const isCategory = categories.some((c) => c._id === id || c.id === id || c.name === id);
    if (isCategory) {
      toggleService(id);
    } else {
      toggleSubService(id);
    }
  };

  return (
    <div className="flex flex-col gap-2.5 sm:gap-3 w-full animate-fadeIn flex-1 pt-1">
      {/* ----------------- PLANNER ----------------- */}
      {role === "planner" && (
        <>
          <ServiceSelectionGrid
            title="What services are you looking for?"
            options={combinedOptions}
            loading={loading}
            selectedServices={combinedSelected}
            onToggle={handleCombinedToggle}
          />

          <div className="pt-0.5">
            <h3 className="text-base sm:text-lg font-bold text-gray-950 mb-2">How many people are you expecting?</h3>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-14 sm:w-16 px-2.5 py-1.5 sm:py-2 border border-gray-200 rounded-xl text-center text-xs sm:text-sm font-semibold text-gray-900 bg-white shrink-0">
                10
              </div>
              <div className="flex-1 relative flex flex-col items-center pt-1">
                <input
                  type="range"
                  min="10"
                  max="10000"
                  value={formData.guests || 10}
                  onChange={(e) => updateField({ guests: parseInt(e.target.value) })}
                  className="w-full h-2 rounded-lg appearance-none accent-[#FF4C18] outline-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #FF4C18 ${(((formData.guests || 10) - 10) / 9990) * 100}%, #E5E7EB ${(((formData.guests || 10) - 10) / 9990) * 100}%)`
                  }}
                />
                <div className="text-[11px] sm:text-xs text-gray-500 font-normal mt-1.5">
                  {formData.guests || 500}
                </div>
              </div>
              <div className="w-16 sm:w-20 px-2.5 py-1.5 sm:py-2 border border-gray-200 rounded-xl text-center text-xs sm:text-sm font-semibold text-gray-400 bg-white shrink-0">
                10000
              </div>
            </div>
          </div>
        </>
      )}

      {/* ----------------- PERFORMER & CREW ----------------- */}
      {(role === "performer" || role === "crew") && (
        <>
          <ServiceSelectionGrid
            title={role === "performer" ? "What type of performer are you looking at?" : "What kind of crew support do you need?"}
            options={combinedOptions}
            loading={loading}
            selectedServices={combinedSelected}
            onToggle={handleCombinedToggle}
          />

          <div className="p-2.5 sm:p-3 bg-gray-50 rounded-xl flex flex-row items-center justify-between border border-gray-100">
            <div>
              <h3 className="text-xs sm:text-sm font-bold text-gray-900">Work Duration</h3>
              <p className="text-[11px] sm:text-xs text-gray-500">
                Estimated Works Hour {formData.isMultiDay && "per day"}
              </p>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Decrement */}
              <button
                type="button"
                onClick={() => updateField({ durationHours: Math.max(1, (parseInt(formData.durationHours) || 1) - 1) })}
                disabled={(parseInt(formData.durationHours) || 1) <= 1}
                className="w-7 h-7 flex items-center justify-center text-gray-700 text-lg font-bold hover:text-[#FF4C18] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                −
              </button>

              {/* Display + editable input */}
              <input
                type="number"
                min="1"
                max="24"
                value={formData.durationHours === '' ? '' : (formData.durationHours ?? 1)}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    updateField({ durationHours: '' });
                  } else {
                    const parsed = parseInt(val, 10);
                    if (!isNaN(parsed)) {
                      updateField({ durationHours: Math.min(24, Math.max(1, parsed)) });
                    }
                  }
                }}
                onBlur={() => {
                  if (!formData.durationHours || formData.durationHours === '' || parseInt(formData.durationHours) < 1) {
                    updateField({ durationHours: 1 });
                  }
                }}
                className="w-14 h-[36px] px-1 border border-gray-200 rounded-lg text-center font-bold text-xs sm:text-sm focus:outline-none focus:border-[#FF4C18] bg-white"
              />

              {/* Increment */}
              <button
                type="button"
                onClick={() => updateField({ durationHours: Math.min(24, (parseInt(formData.durationHours) || 1) + 1) })}
                disabled={(parseInt(formData.durationHours) || 1) >= 24}
                className="w-7 h-7 flex items-center justify-center text-gray-700 text-lg font-bold hover:text-[#FF4C18] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                +
              </button>

              <span className="text-xs font-medium text-gray-700">Hours</span>
            </div>
          </div>

          <div>
            <h3 className="text-xs sm:text-sm font-bold text-gray-900 mb-1.5">Amenities Provided</h3>
            <div className="flex flex-wrap gap-3.5">
              {['food', 'travel', 'accommodation', ...(role === 'crew' ? ['outfit'] : [])].map(amenity => (
                <label key={amenity} className="flex items-center gap-1.5 cursor-pointer transition-colors group">
                  <div className={`w-4 h-4 rounded flex items-center justify-center transition-colors ${formData.amenities[amenity] ? 'bg-black border-black' : 'border border-gray-400 group-hover:border-black'}`}>
                    {formData.amenities[amenity] && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={formData.amenities[amenity]}
                    onChange={(e) => updateField({
                      amenities: { ...formData.amenities, [amenity]: e.target.checked }
                    })}
                  />
                  <span className="capitalize text-xs font-medium text-gray-800">{amenity}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Navigation Buttons */}
      <div className="flex flex-row justify-between items-center gap-2 mt-auto pt-2 sm:pt-3 w-full">
        <button
          type="button"
          onClick={prevStep}
          className="text-gray-400 hover:text-gray-700 font-normal text-xs sm:text-sm flex items-center gap-1.5 transition-colors cursor-pointer"
        >
          &larr; Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="bg-[#FF4C18] hover:bg-[#E63E0F] text-white px-6 py-2 rounded-lg font-medium text-xs sm:text-sm shadow-sm hover:shadow transition-all whitespace-nowrap cursor-pointer"
        >
          Continue &rarr;
        </button>
      </div>

    </div>
  );
}
